import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { type ReactNode, useEffect, useState } from "react";
import { AnalyticsIdentity } from "@/components/analytics/analytics-identity";
import { AppShell } from "@/components/app-shell";
import {
  BOOTSTRAP_STALE_TIME,
  readStoredWorkspaceId,
  useCurrentWorkspace,
  WorkspaceGate,
  WorkspaceProvider,
} from "@/hooks/use-current-workspace";
import { usePublishEvents } from "@/hooks/use-publish-events";
import { getCheckoutIntent } from "@/lib/checkout-intent";
import { createNoIndexHead } from "@/lib/seo";
import { useTRPC } from "@/utils/trpc";

type BootstrapOnboarding = {
  isComplete: boolean;
  welcomed: boolean;
  dismissed: boolean;
} | null;

/** New user still in setup: has progress, hasn't finished/skipped/dismissed it. */
function needsOnboarding(progress: BootstrapOnboarding) {
  return (
    Boolean(progress) &&
    !progress?.isComplete &&
    !progress?.welcomed &&
    !progress?.dismissed
  );
}

/**
 * Onboarding sets a workspace up — posting slots, the first post, teammate
 * invites — so only someone who can administer it belongs in the wizard.
 * Without this, an invited editor or viewer whose landing workspace hadn't
 * finished setup was redirected into a first-run wizard for someone else's
 * workspace, where the invite step would 403 (it is admin-gated).
 */
function canRunOnboarding(
  workspaces: { id: string; role: string }[],
  currentWorkspaceId: string | null,
) {
  const role = workspaces.find((w) => w.id === currentWorkspaceId)?.role;
  return role === "owner" || role === "admin";
}

/**
 * The channel pages stay reachable mid-setup. An AI app's "connect a channel"
 * link lands on /channels/connect, and a provider's OAuth callback returns to
 * /channels. People who sign up through an AI connector never see the wizard,
 * and sending that link to its welcome screen left them two steps from the
 * Connect button: 10 of 14 such sign-ups never connected a channel.
 */
function bypassesOnboarding(pathname: string) {
  return /^\/channels(\/connect)?\/?$/.test(pathname);
}

/**
 * The wizard, keeping the query string it was sent away from. An OAuth connect
 * can come back to an app page with its outcome in the URL (the Page picker's
 * pending id, an error); dropping the query here lost that outcome for good.
 */
function onboardingHref(searchStr: string) {
  return `/onboarding${searchStr}`;
}

function isUnauthorized(cause: unknown) {
  return (
    cause instanceof TRPCClientError &&
    // SAFETY: tRPC puts the error code on `data`, but types it as the
    // server's full error shape, which a client-side helper cannot name. Only
    // `code` is read, and an absent one simply is not `UNAUTHORIZED`.
    (cause.data as { code?: string } | undefined)?.code === "UNAUTHORIZED"
  );
}

export const Route = createFileRoute("/(app)")({
  // Dashboard is client-only — no SEO, and session cookies aren't available to
  // the web Worker anyway. Children inherit this (Selective SSR is one-way).
  ssr: false,
  // One prefetched round-trip drives the whole authenticated shell: the session,
  // the workspace list, and onboarding status — collapsing the old session →
  // workspace → onboarding spinner waterfall into a single loader wait.
  loader: async ({ context: { queryClient, trpc }, location }) => {
    const bootstrap = await queryClient
      .ensureQueryData(
        trpc.viewer.bootstrap.queryOptions({
          workspaceId: readStoredWorkspaceId() ?? undefined,
        }),
      )
      .catch((error) => {
        if (isUnauthorized(error)) {
          throw redirect({
            to: "/login",
            search: {
              redirect: `${location.pathname}${location.searchStr}`,
            },
          });
        }
        throw error;
      });

    if (
      needsOnboarding(bootstrap.onboarding) &&
      canRunOnboarding(bootstrap.workspaces, bootstrap.currentWorkspaceId) &&
      !bypassesOnboarding(location.pathname)
    ) {
      throw redirect({ href: onboardingHref(location.searchStr) });
    }

    return bootstrap;
  },
  head: () => createNoIndexHead("PostDaily"),
  component: RouteComponent,
});

function RouteComponent() {
  const trpc = useTRPC();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const searchStr = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const [bootstrapWorkspaceId] = useState(
    () => readStoredWorkspaceId() ?? undefined,
  );
  const bootstrapQuery = useQuery(
    trpc.viewer.bootstrap.queryOptions(
      { workspaceId: bootstrapWorkspaceId },
      { staleTime: BOOTSTRAP_STALE_TIME },
    ),
  );
  const bootstrap = bootstrapQuery.data;

  // Loader prefetches on client navigations / hydration. Unauthorized covers a
  // missing session on first load and a background refetch that lost it.
  if (!bootstrap) {
    if (bootstrapQuery.isError) {
      if (isUnauthorized(bootstrapQuery.error)) {
        return (
          <Navigate
            to="/login"
            search={{
              redirect: `${pathname}${searchStr}`,
            }}
          />
        );
      }

      return (
        <div className="flex min-h-svh items-center justify-center bg-background p-6">
          <Alert variant="destructive" className="max-w-md">
            <AlertTitle>Could not load your account</AlertTitle>
            <AlertDescription>
              We could not confirm your sign-in state. Try again to continue.
            </AlertDescription>
            <AlertAction>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void bootstrapQuery.refetch();
                }}
              >
                Try again
              </Button>
            </AlertAction>
          </Alert>
        </div>
      );
    }

    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <Spinner className="size-10" />
      </div>
    );
  }

  return (
    <WorkspaceProvider>
      <AnalyticsIdentity />
      <PublishEventsBridge />
      <WorkspaceGate>
        <OnboardingGate>
          <AppShell user={bootstrap.user}>
            <CheckoutIntentGate>
              <Outlet />
            </CheckoutIntentGate>
          </AppShell>
        </OnboardingGate>
      </WorkspaceGate>
    </WorkspaceProvider>
  );
}

/**
 * Renders nothing — holds the workspace-scoped realtime publish-status socket
 * (see use-publish-events) for the lifetime of the authenticated shell.
 */
function PublishEventsBridge() {
  usePublishEvents();
  return null;
}

/**
 * Routes brand-new workspaces into the full-screen onboarding wizard until the
 * user has finished it, skipped it, or completed all steps. Uses `welcomed`
 * (stamped on finish/skip) rather than `isComplete` alone so a user who later
 * disconnects their only account is never bounced back into onboarding.
 *
 * Rendered above AppShell: while progress is loading — or when onboarding is
 * needed — it shows a bare full-page spinner and never mounts the dashboard
 * chrome, so new users don't flash the app shell before the redirect.
 */
function OnboardingGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const searchStr = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const trpc = useTRPC();
  const { currentWorkspace } = useCurrentWorkspace();
  const [bootstrapWorkspaceId] = useState(
    () => readStoredWorkspaceId() ?? undefined,
  );
  const bootstrap = useQuery(
    trpc.viewer.bootstrap.queryOptions(
      { workspaceId: bootstrapWorkspaceId },
      { staleTime: BOOTSTRAP_STALE_TIME },
    ),
  ).data;
  // Seed progress from the already-loaded bootstrap for the resolved workspace,
  // so the common path renders instantly. A workspace the bootstrap didn't cover
  // (e.g. right after switching) still fetches its own progress.
  const seededProgress =
    currentWorkspace?.id &&
    currentWorkspace.id === bootstrap?.currentWorkspaceId
      ? (bootstrap?.onboarding ?? undefined)
      : undefined;
  const progressQuery = useQuery(
    trpc.onboarding.getProgress.queryOptions(
      { workspaceId: currentWorkspace?.id ?? "" },
      {
        enabled: Boolean(currentWorkspace?.id),
        initialData: seededProgress,
        staleTime: BOOTSTRAP_STALE_TIME,
      },
    ),
  );
  const progress = progressQuery.data;
  const gateNeedsOnboarding =
    Boolean(progress) &&
    !progress?.isComplete &&
    !progress?.welcomed &&
    !progress?.dismissed &&
    !bypassesOnboarding(pathname);

  useEffect(() => {
    if (gateNeedsOnboarding) {
      void navigate({ href: onboardingHref(searchStr) });
    }
  }, [gateNeedsOnboarding, navigate, searchStr]);

  if (progressQuery.isPending || gateNeedsOnboarding) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <Spinner className="size-10" />
      </div>
    );
  }

  return children;
}

/**
 * Resumes a pending checkout intent captured on the public pricing page. Only
 * mounts once the user is authenticated and past onboarding (it lives inside
 * OnboardingGate), so a paid-CTA click survives sign-up + the wizard and lands
 * the user on billing, where settings.billing reads the intent and opens Polar
 * checkout. Redirecting (rather than checking out here) keeps all checkout
 * logic in one place and avoids double-firing.
 */
function CheckoutIntentGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  useEffect(() => {
    if (pathname.startsWith("/settings/billing")) {
      return;
    }
    if (!getCheckoutIntent()) {
      return;
    }
    void navigate({ to: "/settings/billing" });
  }, [pathname, navigate]);

  return children;
}
